import React, { useContext, useEffect, useMemo, useState } from "react";
import useQuery from "utils/useQuery";
import { StatusType } from "../SignInHeader/components/Statics";
import { CourseContext } from "../../../Store";
import useRequest from "utils/useRequest";
import {
    getSignInfo,
    getStuSignInList,
    getTeaSignIn,
    SignStatus,
    SignType,
    StuSignInStatus
} from "service/signIn";
import _ from "underscore";
import style from "./index.module.scss";
import SignInHeader from "../SignInHeader";
import NumScreen from "../NumScreen";
import SignInTable from "../SignInTable";
import useChannel from "utils/useChannel";
import { toNumber } from "service/service";
import { SignInMessage } from "../../../components/ProjectionLayout/components/SignInModal";
import { StuSignIn } from "service/signIn";
import { errorMessage } from "service/service";
import { getStuOnlineSignInList } from "service/signIn";
import { ResultMessage } from "components/ResultMessage";

export interface FilterValue {
    status: StatusType; // 全部/已签到/未签到
    text: string;
}

interface SignInDetailProps {
    signInId: number;
}

export default function SignInDetail({ signInId }: SignInDetailProps) {
    const { status = "all" } = useQuery<{ status: StatusType }>();

    const { course } = useContext(CourseContext);
    const [data, setData] = useState<StuSignIn[]>([]);

    const [detail, detailStatus, updateDetail] = useRequest(
        () => getTeaSignIn(signInId),
        {},
        [signInId]
    );
    const [signInfo, signInfoStatus, updateSignInfo] = useRequest(() =>
        getSignInfo(signInId)
    );
    const [filter, setFilter] = useState<FilterValue>({ status, text: "" });

    // 投屏时如果在投票列表里结束活动, 正在进行中的该投票也同时刷新状态
    useChannel<SignInMessage>("signIn", message => {
        if (
            message.type === "onEnd" &&
            message.signInId === toNumber(signInId)
        ) {
            updateInfo();
        }
    });

    // 显示数字码的条件
    const showNumScreen =
        detail?.type === SignType.Number &&
        detail.status === SignStatus.InProgress;

    useEffect(() => {
        if (!detail) return;
        getAllStuList(detail.type);
    }, [detail]);

    async function getAllStuList(type: SignType) {
        try {
            if (type === SignType.Live) {
                const data = await getStuOnlineSignInList(signInId);
                setData(data);
            } else {
                const data = await getStuSignInList(signInId);
                setData(data);
            }
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }

    // 统计签到人数
    const statics = useMemo(() => {
        const statics = {
            courseStuCount: data?.length || 0,
            signInStuCount: 0,
            notSingInStuCount: 0
        };

        _.forEach(data || [], item => {
            if (
                [
                    StuSignInStatus.Done,
                    StuSignInStatus.Leave,
                    StuSignInStatus.Late
                ].includes(item.signStatus)
            )
                statics.signInStuCount++;
            else statics.notSingInStuCount++;
        });

        return statics;
    }, [data]);

    // 筛选数据
    const filteredData = useMemo(() => {
        if (!data) return [];

        return data
            .filter(item => {
                // 过滤输入框
                if (item.name.indexOf(filter.text) !== -1) return true;
                if (item.studentno === filter.text) return true;
                return false;
            })
            .filter(item => {
                // 签到状态
                if (filter.status === "all") return true;
                if (
                    filter.status === "sign" &&
                    [
                        StuSignInStatus.Late,
                        StuSignInStatus.Done,
                        StuSignInStatus.Leave
                    ].includes(item.signStatus)
                )
                    return true;
                if (
                    filter.status === "unSign" &&
                    [StuSignInStatus.Idle].includes(item.signStatus)
                )
                    return true;
                return false;
            });
    }, [data, filter]);

    // 更新列表状态和统计信息
    function updateList() {
        getAllStuList(detail!.type);
        updateSignInfo();
    }

    // 更新统计信息和签到状态
    async function updateInfo() {
        await updateDetail();
        await updateSignInfo();
    }

    return (
        <div className={style.root}>
            <SignInHeader
                weekTimeId={course.weekTimeId}
                signInId={signInId}
                align={showNumScreen ? "right" : "left"}
                status={detail?.status || SignStatus.Idle}
                signInfo={signInfo}
                onStatusChange={updateInfo}
                statics={statics}
                onFilterStatusChange={status =>
                    setFilter({ ...filter, status })
                }
                onFilterTextChange={text => setFilter({ ...filter, text })}
                onCountDown={updateInfo}
            />

            <div className={style.body}>
                {showNumScreen && (
                    <NumScreen
                        signInId={signInId}
                        code={signInfo?.code || ""}
                        refreshTime={signInfo?.refreshTime || 10}
                        stuSignList={filteredData || []}
                    />
                )}
                {!showNumScreen && (
                    <SignInTable
                        signInId={signInId}
                        data={filteredData || []}
                        signStatus={detail?.status || SignStatus.Idle}
                        onChange={updateList}
                        type={detail?.type}
                    />
                )}
            </div>
        </div>
    );
}
